import { Injectable, InjectionToken, inject } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';

import { environment } from '../../../environments/environment';

export type CadixProgressPhase = 'thinking' | 'retrieving' | 'composing';
export const CADIX_THINKING_MESSAGE = 'CADIX is working.';

export interface CadixConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export type CadixStreamEvent =
  | {
      type: 'accepted';
      turnId: string;
      conversationId: string;
    }
  | {
      type: 'progress';
      phase: CadixProgressPhase;
      message: string;
    }
  | {
      type: 'delta';
      text: string;
    }
  | {
      type: 'done';
      turnId: string;
      conversationId: string;
      reply: string;
      activeJobId: string | null;
      messages: CadixConversationMessage[];
    }
  | {
      type: 'error';
      turnId: string;
      code: CadixStreamErrorCode;
      message: string;
      retryable: true;
    };

export type CadixStreamErrorCode =
  | 'agent_unavailable'
  | 'agent_failed'
  | 'persistence_failed'
  | 'invalid_stream';

export interface CadixStream {
  /** HTTP 200 headers were received, so an atomic fallback is no longer safe. */
  readonly responseAccepted: true;
  readonly events: AsyncIterable<CadixStreamEvent>;
}

export type CadixFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export const CADIX_FETCH = new InjectionToken<CadixFetch>('CADIX_FETCH', {
  providedIn: 'root',
  factory: () => globalThis.fetch.bind(globalThis),
});

export class CadixStreamHttpError extends Error {
  readonly responseAccepted = false;

  constructor(readonly status: number) {
    super(`CADIX stream request failed with HTTP ${status}`);
    this.name = 'CadixStreamHttpError';
  }
}

export class CadixStreamAuthError extends Error {
  readonly responseAccepted = false;

  constructor() {
    super('CADIX access token is unavailable');
    this.name = 'CadixStreamAuthError';
  }
}

export class CadixStreamProtocolError extends Error {
  readonly responseAccepted = true;

  constructor(message: string) {
    super(message);
    this.name = 'CadixStreamProtocolError';
  }
}

export class CadixStreamTerminalError extends Error {
  readonly responseAccepted = true;

  constructor(
    readonly code: CadixStreamErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'CadixStreamTerminalError';
  }
}

const MAX_EVENT_DATA_BYTES = 64 * 1024;
const MAX_ASSISTANT_DELTA_BYTES = 256 * 1024;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const PROGRESS_MESSAGES: Record<CadixProgressPhase, string> = {
  thinking: CADIX_THINKING_MESSAGE,
  retrieving: 'CADIX is checking authorized data.',
  composing: 'CADIX is preparing the response.',
};

const ERROR_MESSAGES: Record<CadixStreamErrorCode, string> = {
  agent_unavailable: 'CADIX is temporarily unavailable. Try again.',
  agent_failed: 'CADIX could not complete the request. Try again.',
  persistence_failed: 'CADIX generated a response but could not save it. Try again.',
  invalid_stream: 'CADIX returned an invalid response. Try again.',
};

@Injectable({ providedIn: 'root' })
export class CadixStreamClient {
  private readonly oktaAuth = inject(OKTA_AUTH, { optional: true });
  private readonly fetchImpl = inject(CADIX_FETCH);

  async open(
    conversationUrl: string,
    message: string,
    turnId: string,
    signal: AbortSignal,
    rowFocusIds: readonly string[] = [],
  ): Promise<CadixStream> {
    if (!UUID_PATTERN.test(turnId)) {
      throw new Error('CADIX turnId must be a UUID');
    }

    const accessToken = await this.oktaAuth?.getOrRenewAccessToken();
    if (!accessToken && environment.authenticationRequired) {
      throw new CadixStreamAuthError();
    }
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const response = await this.fetchImpl(`${conversationUrl}/messages/stream`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        turnId,
        ...(rowFocusIds.length ? { rowFocusIds } : {}),
      }),
      signal,
    });

    if (!response.ok) {
      throw new CadixStreamHttpError(response.status);
    }
    if (response.status !== 200) {
      throw new CadixStreamProtocolError('CADIX stream returned an unexpected success status');
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!/^text\/event-stream(?:\s*;|$)/i.test(contentType)) {
      throw new CadixStreamProtocolError('CADIX stream returned an invalid content type');
    }
    if (!response.body) {
      throw new CadixStreamProtocolError('CADIX stream returned no response body');
    }

    return {
      responseAccepted: true,
      events: this.parseEvents(response.body, turnId, conversationUrl),
    };
  }

  private async *parseEvents(
    body: ReadableStream<Uint8Array>,
    requestedTurnId: string,
    conversationUrl: string,
  ): AsyncGenerator<CadixStreamEvent> {
    const requestedConversationId = this.conversationIdFromUrl(conversationUrl);
    const reader = body.getReader();
    const decoder = new TextDecoder('utf-8', { fatal: true });
    const byteCounter = new TextEncoder();
    let buffer = '';
    let eventName = '';
    let dataLines: string[] = [];
    let accepted = false;
    let terminal: CadixStreamEvent | null = null;
    let cumulativeDeltaBytes = 0;

    const resetFrame = (): void => {
      eventName = '';
      dataLines = [];
    };

    const dispatch = (): CadixStreamEvent | null => {
      if (dataLines.length === 0) {
        resetFrame();
        return null;
      }
      if (terminal) {
        throw new CadixStreamProtocolError('CADIX stream emitted an event after termination');
      }

      const data = dataLines.join('\n');
      const dispatchedEventName = eventName || 'message';
      resetFrame();
      if (byteCounter.encode(data).byteLength > MAX_EVENT_DATA_BYTES) {
        throw new CadixStreamProtocolError('CADIX stream event payload is too large');
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        throw new CadixStreamProtocolError('CADIX stream event contains invalid JSON');
      }

      const event = this.validateEvent(
        dispatchedEventName,
        parsed,
        requestedTurnId,
        requestedConversationId,
      );
      if (event.type === 'accepted') {
        if (accepted) {
          throw new CadixStreamProtocolError('CADIX stream emitted accepted more than once');
        }
        accepted = true;
      } else if (!accepted) {
        throw new CadixStreamProtocolError(
          `CADIX stream emitted ${event.type} before accepted`,
        );
      }

      if (event.type === 'delta') {
        cumulativeDeltaBytes += byteCounter.encode(event.text).byteLength;
        if (cumulativeDeltaBytes > MAX_ASSISTANT_DELTA_BYTES) {
          throw new CadixStreamProtocolError('CADIX assistant output is too large');
        }
      } else if (event.type === 'done' || event.type === 'error') {
        terminal = event;
        return null;
      }
      return event;
    };

    const processLine = (line: string): CadixStreamEvent | null => {
      if (line.endsWith('\r')) {
        line = line.slice(0, -1);
      }
      if (line === '') {
        return dispatch();
      }
      if (line.startsWith(':')) {
        return null;
      }
      const separator = line.indexOf(':');
      const field = separator < 0 ? line : line.slice(0, separator);
      let value = separator < 0 ? '' : line.slice(separator + 1);
      if (value.startsWith(' ')) {
        value = value.slice(1);
      }
      if (field === 'event') {
        eventName = value;
      } else if (field === 'data') {
        dataLines.push(value);
        if (byteCounter.encode(dataLines.join('\n')).byteLength > MAX_EVENT_DATA_BYTES) {
          throw new CadixStreamProtocolError('CADIX stream event payload is too large');
        }
      }
      return null;
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          buffer += decoder.decode();
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        if (byteCounter.encode(buffer).byteLength > MAX_EVENT_DATA_BYTES + 1024) {
          throw new CadixStreamProtocolError('CADIX stream line is too large');
        }
        let newline = buffer.indexOf('\n');
        while (newline >= 0) {
          const event = processLine(buffer.slice(0, newline));
          buffer = buffer.slice(newline + 1);
          if (event) {
            yield event;
          }
          newline = buffer.indexOf('\n');
        }
      }

      if (buffer) {
        processLine(buffer);
      }
      if (!terminal) {
        throw new CadixStreamProtocolError('CADIX stream ended before a terminal event');
      }
      yield terminal;
    } catch (error) {
      if (error instanceof CadixStreamProtocolError || this.isAbortError(error)) {
        throw error;
      }
      throw new CadixStreamProtocolError('CADIX stream could not be decoded');
    } finally {
      reader.releaseLock();
    }
  }

  private validateEvent(
    name: string,
    value: unknown,
    requestedTurnId: string,
    requestedConversationId: string,
  ): CadixStreamEvent {
    if (name === 'accepted') {
      this.requireExactObject(value, ['turnId', 'conversationId']);
      this.requireTurnId(value['turnId'], requestedTurnId);
      this.requireConversationId(value['conversationId'], requestedConversationId);
      return {
        type: 'accepted',
        turnId: value['turnId'],
        conversationId: value['conversationId'],
      };
    }
    if (name === 'progress') {
      this.requireExactObject(value, ['phase', 'message']);
      const phase = value['phase'];
      if (phase !== 'thinking' && phase !== 'retrieving' && phase !== 'composing') {
        throw new CadixStreamProtocolError('CADIX stream progress phase is invalid');
      }
      if (value['message'] !== PROGRESS_MESSAGES[phase]) {
        throw new CadixStreamProtocolError('CADIX stream progress message is invalid');
      }
      return { type: 'progress', phase, message: value['message'] };
    }
    if (name === 'delta') {
      this.requireExactObject(value, ['text']);
      if (typeof value['text'] !== 'string') {
        throw new CadixStreamProtocolError('CADIX stream delta is invalid');
      }
      return { type: 'delta', text: value['text'] };
    }
    if (name === 'done') {
      return this.validateDone(value, requestedTurnId, requestedConversationId);
    }
    if (name === 'error') {
      return this.validateError(value, requestedTurnId);
    }
    throw new CadixStreamProtocolError(`CADIX stream event type ${name} is unsupported`);
  }

  private validateDone(
    value: unknown,
    requestedTurnId: string,
    requestedConversationId: string,
  ): Extract<CadixStreamEvent, { type: 'done' }> {
    this.requireExactObject(value, [
      'turnId',
      'conversationId',
      'reply',
      'activeJobId',
      'messages',
    ]);
    this.requireTurnId(value['turnId'], requestedTurnId);
    this.requireConversationId(value['conversationId'], requestedConversationId);
    if (
      typeof value['reply'] !== 'string' ||
      value['reply'].length < 1 ||
      value['reply'].length > 16_000 ||
      !(value['activeJobId'] === null || typeof value['activeJobId'] === 'string') ||
      !this.isMessages(value['messages'])
    ) {
      throw new CadixStreamProtocolError('CADIX stream done payload is invalid');
    }
    return {
      type: 'done',
      turnId: value['turnId'],
      conversationId: value['conversationId'],
      reply: value['reply'],
      activeJobId: value['activeJobId'],
      messages: value['messages'],
    };
  }

  private validateError(
    value: unknown,
    requestedTurnId: string,
  ): Extract<CadixStreamEvent, { type: 'error' }> {
    this.requireExactObject(value, ['turnId', 'code', 'message', 'retryable']);
    this.requireTurnId(value['turnId'], requestedTurnId);
    const code = value['code'];
    if (
      (code !== 'agent_unavailable' &&
        code !== 'agent_failed' &&
        code !== 'persistence_failed' &&
        code !== 'invalid_stream') ||
      value['message'] !== ERROR_MESSAGES[code] ||
      value['retryable'] !== true
    ) {
      throw new CadixStreamProtocolError('CADIX stream error payload is invalid');
    }
    return {
      type: 'error',
      turnId: value['turnId'],
      code,
      message: value['message'],
      retryable: true,
    };
  }

  private requireExactObject(
    value: unknown,
    keys: readonly string[],
  ): asserts value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new CadixStreamProtocolError('CADIX stream payload is not an object');
    }
    const actual = Object.keys(value).sort((left, right) => left.localeCompare(right));
    const expected = [...keys].sort((left, right) => left.localeCompare(right));
    if (
      actual.length !== expected.length ||
      actual.some((key, index) => key !== expected[index])
    ) {
      throw new CadixStreamProtocolError('CADIX stream payload shape is invalid');
    }
  }

  private requireTurnId(value: unknown, requestedTurnId: string): asserts value is string {
    if (typeof value !== 'string' || !UUID_PATTERN.test(value) || value !== requestedTurnId) {
      throw new CadixStreamProtocolError('CADIX stream turnId does not match');
    }
  }

  private requireConversationId(
    value: unknown,
    requestedConversationId: string,
  ): asserts value is string {
    if (typeof value !== 'string' || value !== requestedConversationId) {
      throw new CadixStreamProtocolError('CADIX stream conversationId does not match');
    }
  }

  private isMessages(value: unknown): value is CadixConversationMessage[] {
    return (
      Array.isArray(value) &&
      value.every((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          return false;
        }
        const candidate = item as Record<string, unknown>;
        return (
          Object.keys(candidate)
            .sort((left, right) => left.localeCompare(right))
            .join(',') === 'content,createdAt,role' &&
          (candidate['role'] === 'user' || candidate['role'] === 'assistant') &&
          typeof candidate['content'] === 'string' &&
          candidate['content'].length >= 1 &&
          candidate['content'].length <= 16_000 &&
          typeof candidate['createdAt'] === 'string'
        );
      })
    );
  }

  private conversationIdFromUrl(conversationUrl: string): string {
    const path = conversationUrl.split(/[?#]/, 1)[0];
    const segment = path.slice(path.lastIndexOf('/') + 1);
    try {
      return decodeURIComponent(segment);
    } catch {
      throw new Error('CADIX conversation URL is invalid');
    }
  }

  private isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
  }
}
